import { Link } from "wouter";
import { Container } from "@/components/ui/container";
import { Facebook, Instagram, Twitter, Linkedin, MapPin, Phone, Mail, Youtube } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useAppSettings, useBrandingSettings } from "@/contexts/AppSettingsContext";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getSchemaScopedQueryKey } from "@/lib/schemaStorage";
import { fetchWithSchema } from "@/lib/api";
import type { Service } from "@webready/shared/schema";
import { useMemo } from "react";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";
import { useNavigationLinks } from "@/hooks/useNavigationLinks";
import { cn } from "@/lib/utils";
import { useCustomSiteBackdrop } from "@/hooks/useCustomSiteBackdrop";
import WebReadyLegalFooter from "@/components/common/WebReadyLegalFooter";
import { isWebReadyTenant, WEBREADY_LEGAL_ROUTES } from "@/lib/webreadyLegal";

export default function Footer() {
  const { settings } = useAppSettings();
  const brandingSettings = useBrandingSettings();
  const { setShowBanner } = useCookieConsent();
  const { t } = useTranslation();
  const { isFeatureEnabled } = useSubscription();
  const { appConfig } = useAppConfiguration();
  const { isActive: useDarkGlassFooter } = useCustomSiteBackdrop();

  const isBookingEnabled = isFeatureEnabled("enable_booking");
  const isPresentationMode = appConfig?.appointmentType === "website_presentation";
  const showServicesColumn = isBookingEnabled || isPresentationMode;
  const { links: footerLinks } = useNavigationLinks();

  const headingClass = useDarkGlassFooter
    ? "text-white"
    : "text-foreground";
  const bodyClass = useDarkGlassFooter
    ? "text-white"
    : "text-muted-foreground";
  const linkClass = useDarkGlassFooter
    ? "text-white hover:text-white/80 transition-colors"
    : "text-muted-foreground hover:text-foreground transition-colors";
  const mutedClass = useDarkGlassFooter
    ? "text-white"
    : "text-muted-foreground";
  const iconClass = useDarkGlassFooter
    ? "text-white"
    : "text-muted-foreground";
  const separatorClass = useDarkGlassFooter
    ? "bg-white/20"
    : "bg-border";
  const skeletonClass = useDarkGlassFooter
    ? "bg-white/20"
    : "bg-muted";

  const { data: allServicesRaw = [], isLoading: isLoadingServices } = useQuery<Service[]>({
    queryKey: getSchemaScopedQueryKey(["services"]),
    queryFn: async () => {
      const res = await fetchWithSchema("/api/v1/services");
      if (!res.ok) throw new Error("Failed to fetch services");
      const data = await res.json();

      let services: Service[] = [];
      if (Array.isArray(data)) {
        services = data;
      } else if (data && Array.isArray(data.data)) {
        services = data.data;
      } else if (data && Array.isArray(data.results)) {
        services = data.results;
      }

      return services;
    },
    enabled: showServicesColumn,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const filteredServices = useMemo(() => {
    if (!allServicesRaw || allServicesRaw.length === 0) return [];

    if (appConfig?.appointmentType === "clinic_hours") {
      return allServicesRaw.filter(
        (s: Service) => s.active && s.service_type === "clinic_hours"
      );
    }

    if (appConfig?.appointmentType === "lodging_days") {
      return allServicesRaw.filter(
        (s: Service) => s.active && s.service_type === "lodging_days"
      );
    }

    if (appConfig?.appointmentType === "website_presentation") {
      return allServicesRaw.filter(
        (s: Service) =>
          s.active &&
          (s.service_type === "website_presentation" ||
            s.service_type === undefined ||
            s.service_type === null)
      );
    }

    return allServicesRaw.filter((s: Service) => s.active);
  }, [allServicesRaw, appConfig]);

  const services = useMemo(() => {
    const dominantService = filteredServices.find(s => s.id === 55);
    const servicesWithoutDominant = filteredServices.filter(s => s.id !== 55);
    const featuredServices = servicesWithoutDominant.slice(-3);
    
    if (dominantService) {
      return [dominantService, ...featuredServices];
    }
    return featuredServices.slice(0, 4);
  }, [filteredServices]);

  const handleCookieSettings = () => {
    setShowBanner(true);
    window.dispatchEvent(new CustomEvent("cookie-consent:open"));
  };

  const handlePrivacyPolicy = () => {
    window.dispatchEvent(new CustomEvent("privacy-policy:open"));
  };

  const handleTermsOfService = () => {
    window.dispatchEvent(new CustomEvent("terms-of-service:open"));
  };

  const gridColsClass = "grid-cols-1 sm:grid-cols-2 md:grid-cols-4";

  return (
    <footer
      data-footer-variant={useDarkGlassFooter ? "dark-glass" : "default-glass"}
      className={cn(
        "md:min-h-[450px] transition-[background-color,backdrop-filter,border-color] duration-300",
        useDarkGlassFooter
          ? "border-t border-white/10 bg-black/80 text-white backdrop-blur-md supports-[backdrop-filter]:bg-black/75"
          : "border-t border-border bg-background/95 text-foreground backdrop-blur supports-[backdrop-filter]:bg-background/60"
      )}
    >
      <Container className="px-4 sm:px-6 lg:px-8">
        <div className={`grid ${gridColsClass} gap-6 md:gap-8 pt-4 md:pt-4 md:pb-12`}>
          <div>
            <h3 className={cn("text-base md:text-lg font-bold mb-3 md:mb-4", headingClass)}>
              {settings?.app_name || ""}
            </h3>
            {brandingSettings.companyDescription && (
              <p className={cn("text-xs md:text-sm", bodyClass)}>
                {brandingSettings.companyDescription}
              </p>
            )}
            {brandingSettings.tagline && (
              <p className={cn("text-xs md:text-sm font-medium mt-2", bodyClass)}>
                &ldquo;{brandingSettings.tagline}&rdquo;
              </p>
            )}
          </div>

          <div style={{ minHeight: "120px" }}>
            {showServicesColumn ? (
              <>
                <h4 className={cn("text-sm md:text-base font-semibold mb-3 md:mb-4", headingClass)}>
                  {t("public.footer.services", "Servicii")}
                </h4>
                {isLoadingServices ? (
                  <ul className="space-y-2 md:space-y-2 text-xs md:text-sm">
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i} className="min-h-[44px] py-1.5 flex items-center">
                        <div className={cn("h-4 rounded w-3/4 animate-pulse", skeletonClass)} />
                      </li>
                    ))}
                  </ul>
                ) : services.length > 0 ? (
                  <ul className="space-y-2 md:space-y-2 text-xs md:text-sm">
                    {services.map((service) => (
                      <li key={service.id}>
                        <Link
                          href={`/services/${service.id}`}
                          className={cn(
                            linkClass,
                            "block min-h-[44px] py-1.5 flex items-center [touch-action:manipulation]"
                          )}
                        >
                          {service.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="space-y-2 text-sm">
                    <li className={mutedClass}>
                      {t("public.footer.noServices", "Nu sunt servicii disponibile")}
                    </li>
                  </ul>
                )}
              </>
            ) : (
              <div aria-hidden="true" />
            )}
          </div>

          <div>
            <h4 className={cn("text-sm md:text-base font-semibold mb-3 md:mb-4", headingClass)}>
              {t("public.footer.quickLinks", "Link-uri rapide")}
            </h4>
            {footerLinks && footerLinks.length > 0 ? (
              <ul className="space-y-2 md:space-y-2 text-xs md:text-sm">
                {footerLinks.map((link, index) => (
                  <li key={`${link.href}-${index}`}>
                    <Link
                      href={link.href}
                      className={cn(
                        linkClass,
                        "block min-h-[44px] py-1.5 flex items-center [touch-action:manipulation]"
                      )}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    href="/site"
                    className={cn(
                      linkClass,
                      "block min-h-[44px] py-1.5 flex items-center [touch-action:manipulation]"
                    )}
                  >
                    Servicii și Locații
                  </Link>
                </li>
              </ul>
            ) : null}
          </div>

          <div>
            <h4 className={cn("text-sm md:text-base font-semibold mb-3 md:mb-4", headingClass)}>
              {t("public.footer.contact", "Contact")}
            </h4>
            <div className="space-y-2 md:space-y-2.5 text-xs md:text-sm">
              {brandingSettings.contactEmail && (
                <div className="flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <Mail className={cn("h-4 w-4 flex-shrink-0", iconClass)} />
                  <a
                    href={`mailto:${brandingSettings.contactEmail}`}
                    className={cn(
                      linkClass,
                      "flex-1 min-h-[44px] py-1.5 flex items-center [touch-action:manipulation]"
                    )}
                  >
                    {brandingSettings.contactEmail}
                  </a>
                </div>
              )}
              {brandingSettings.socialMedia?.facebook && (
                <div className="flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <Facebook className={cn("h-4 w-4 flex-shrink-0", iconClass)} />
                  <a
                    href={brandingSettings.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(linkClass, "py-1.5")}
                  >
                    Facebook
                  </a>
                </div>
              )}
              {brandingSettings.contactPhone && (
                <div className="flex items-center gap-2 min-h-[44px] md:min-h-0">
                  <Phone className={cn("h-4 w-4 flex-shrink-0", iconClass)} />
                  <a
                    href={`tel:${brandingSettings.contactPhone}`}
                    className={cn(
                      linkClass,
                      "flex-1 min-h-[44px] py-1.5 flex items-center [touch-action:manipulation]"
                    )}
                  >
                    {brandingSettings.contactPhone}
                  </a>
                </div>
              )}
              {brandingSettings.address && (
                <div className="flex items-start gap-2 min-h-[44px] md:min-h-0">
                  <MapPin className={cn("h-4 w-4 mt-1.5 flex-shrink-0", iconClass)} />
                  <span className={cn(bodyClass, "py-1.5")}>{brandingSettings.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <Separator className={separatorClass} />

        <div className="flex flex-col md:flex-row justify-between items-center py-4 md:py-6">
          <div className="flex flex-row flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-2 text-xs md:text-sm min-h-[44px]">
            <p className={cn("shrink-0", mutedClass)}>
              © {new Date().getFullYear()} {settings?.app_name || ""}.{" "}
              {t("public.footer.allRightsReserved", "Toate drepturile rezervate.")}
            </p>

            {isWebReadyTenant() ? (
              <>
                <span className={cn("hidden sm:inline", mutedClass)} aria-hidden>
                  ·
                </span>
                <Link
                  href={WEBREADY_LEGAL_ROUTES.terms}
                  className={cn(linkClass, "min-h-[44px] px-1 py-1.5 flex items-center [touch-action:manipulation]")}
                >
                  {t("public.footer.legal.terms", "Termeni și Condiții")}
                </Link>
                <span className={cn("hidden sm:inline", mutedClass)} aria-hidden>
                  ·
                </span>
                <Link
                  href={WEBREADY_LEGAL_ROUTES.privacy}
                  className={cn(linkClass, "min-h-[44px] px-1 py-1.5 flex items-center [touch-action:manipulation]")}
                >
                  {t("public.footer.legal.privacy", "Politica de Confidențialitate")}
                </Link>
                <span className={cn("hidden sm:inline", mutedClass)} aria-hidden>
                  ·
                </span>
                <Link
                  href={WEBREADY_LEGAL_ROUTES.cookies}
                  className={cn(linkClass, "min-h-[44px] px-1 py-1.5 flex items-center [touch-action:manipulation]")}
                >
                  {t("public.footer.legal.cookies", "Politica Cookie-uri")}
                </Link>
                <span className={cn("hidden sm:inline", mutedClass)} aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  onClick={handleCookieSettings}
                  className={cn(linkClass, "min-h-[44px] px-1 py-1.5 flex items-center [touch-action:manipulation]")}
                >
                  {t("cookieConsent.footer.cookieSettings", "Setări Cookie-uri")}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleCookieSettings}
                  className={cn(
                    linkClass,
                    "min-h-[44px] px-2 py-1.5 flex items-center [touch-action:manipulation]"
                  )}
                >
                  {t("cookieConsent.footer.cookieSettings")}
                </button>
                <button
                  onClick={handlePrivacyPolicy}
                  className={cn(
                    linkClass,
                    "min-h-[44px] px-2 py-1.5 flex items-center [touch-action:manipulation]"
                  )}
                >
                  {t("cookieConsent.footer.privacyPolicy")}
                </button>
                <button
                  onClick={handleTermsOfService}
                  className={cn(
                    linkClass,
                    "min-h-[44px] px-2 py-1.5 flex items-center [touch-action:manipulation]"
                  )}
                >
                  {t("cookieConsent.footer.termsOfService")}
                </button>
              </>
            )}
          </div>

          {Object.keys(brandingSettings.socialMedia).length > 0 && (
            <div className="flex space-x-3 md:space-x-4 mt-4 md:mt-0">
              {brandingSettings.socialMedia.facebook && (
                <a
                  href={brandingSettings.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    linkClass,
                    "min-h-[44px] min-w-[44px] flex items-center justify-center [touch-action:manipulation]"
                  )}
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {brandingSettings.socialMedia.instagram && (
                <a
                  href={brandingSettings.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    linkClass,
                    "min-h-[44px] min-w-[44px] flex items-center justify-center [touch-action:manipulation]"
                  )}
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {brandingSettings.socialMedia.twitter && (
                <a
                  href={brandingSettings.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    linkClass,
                    "min-h-[44px] min-w-[44px] flex items-center justify-center [touch-action:manipulation]"
                  )}
                  aria-label="Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {brandingSettings.socialMedia.linkedin && (
                <a
                  href={brandingSettings.socialMedia.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    linkClass,
                    "min-h-[44px] min-w-[44px] flex items-center justify-center [touch-action:manipulation]"
                  )}
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {brandingSettings.socialMedia.youtube && (
                <a
                  href={brandingSettings.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    linkClass,
                    "min-h-[44px] min-w-[44px] flex items-center justify-center [touch-action:manipulation]"
                  )}
                  aria-label="YouTube"
                >
                  <Youtube className="h-5 w-5" />
                </a>
              )}
            </div>
          )}
        </div>

        <WebReadyLegalFooter linkClass={linkClass} />
      </Container>
    </footer>
  );
}
